const defaultShaderType: Array<keyof WebGLRenderingContext> = ["VERTEX_SHADER", "FRAGMENT_SHADER"];

function createProgram(
    gl: WebGLRenderingContext,
    shaders: Array<WebGLShader | null>,
    _opt_attribs: string[] | null,
    _opt_locations: number[],
    opt_errorCallback: (msg: string) => void,
): WebGLProgram | null {
    const errFn = opt_errorCallback;
    const program = gl.createProgram();
    shaders.forEach(function (shader) {
        if (shader) {
            gl.attachShader(program, shader);
        }
    });
    gl.linkProgram(program);
    // Check the link status
    const linked = gl.getProgramParameter(program, gl.LINK_STATUS);
    if (!linked) {
        // something went wrong with the link
        const lastError = gl.getProgramInfoLog(program);
        errFn("Error in program linking:" + lastError);
        gl.deleteProgram(program);
        return null;
    }
    return program;
}
function loadShader(
    gl: WebGLRenderingContext,
    shaderSource: string,
    shaderType: number,
    opt_errorCallback: (msg: string) => void,
): WebGLShader | null {
    const errFn = opt_errorCallback;
    // Create the shader object
    const shader = gl.createShader(shaderType)!;
    // Load the shader source
    gl.shaderSource(shader, shaderSource);
    // Compile the shader
    gl.compileShader(shader);
    // Check the compile status
    const compiled = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
    if (!compiled) {
        // Something went wrong during compilation; get the error
        const lastError = gl.getShaderInfoLog(shader);
        errFn("*** Error compiling shader '" + shader + "':" + lastError);
        gl.deleteShader(shader);
        return null;
    }
    return shader;
}

function createProgramFromSources(
    gl: WebGLRenderingContext,
    shaderSources: string[],
    opt_attribs?: any,
    opt_locations?: any,
    opt_errorCallback?: any,
): WebGLProgram | null {
    const shaders = [];
    for (let ii = 0; ii < shaderSources.length; ++ii) {
        shaders.push(
            // @ts-ignore
            loadShader(gl, shaderSources[ii], gl[defaultShaderType[ii]], opt_errorCallback),
        );
    }
    return createProgram(gl, shaders, opt_attribs, opt_locations, opt_errorCallback);
}

const vertexShaderSource =
    "attribute vec2 a_position;" +
    "attribute vec2 a_texCoord;" +
    "uniform vec2 u_resolution;" +
    "varying vec2 v_texCoord;" +
    "void main() {" +
    "vec2 zeroToOne = a_position / u_resolution;" +
    "   vec2 zeroToTwo = zeroToOne * 2.0;" +
    "   vec2 clipSpace = zeroToTwo - 1.0;" +
    "   gl_Position = vec4(clipSpace * vec2(1, -1), 0, 1);" +
    "v_texCoord = a_texCoord;" +
    "}";
const yuvShaderSource =
    "precision mediump float;" +
    "uniform sampler2D Ytex;" +
    "uniform sampler2D Utex,Vtex;" +
    "varying vec2 v_texCoord;" +
    "void main(void) {" +
    "  float nx,ny,r,g,b,y,u,v;" +
    "  mediump vec4 txl,ux,vx;" +
    "  nx=v_texCoord[0];" +
    "  ny=v_texCoord[1];" +
    "  y=texture2D(Ytex,vec2(nx,ny)).r;" +
    "  u=texture2D(Utex,vec2(nx,ny)).r;" +
    "  v=texture2D(Vtex,vec2(nx,ny)).r;" +
    "  y=1.1643*(y-0.0625);" +
    "  u=u-0.5;" +
    "  v=v-0.5;" +
    "  r=y+1.5958*v;" +
    "  g=y-0.39173*u-0.81290*v;" +
    "  b=y+2.017*u;" +
    "  gl_FragColor=vec4(r,g,b,1.0);" +
    "}";
const createGL = function (canvas: HTMLCanvasElement): WebGLRenderingContext | null {
    const errMsg = "Create WebGL context failed";
    let isSuccess = true;
    let gl = null;
    try {
        gl =
            canvas.getContext("webgl", { preserveDrawingBuffer: true }) ||
            canvas.getContext("experimental-webgl");
    } catch (err) {
        isSuccess = false;
    }
    if (!isSuccess || !gl) {
        throw new Error(errMsg);
    }
    return gl as WebGL2RenderingContext;
};
export class I420Canvas3DRenderer {
    private positionLocation: number | null = null;
    private texCoordLocation: number | null = null;
    private surfaceBuffer: WebGLBuffer | null = null;
    private texCoordBuffer: WebGLBuffer | null = null;
    private yTexture: WebGLTexture | null = null;
    private uTexture: WebGLTexture | null = null;
    private vTexture: WebGLTexture | null = null;
    private gl: WebGLRenderingContext | null = null;
    private glProgram: WebGLProgram | null = null;
    public constructor(canvas: HTMLCanvasElement) {
        this.positionLocation = null;
        this.texCoordLocation = null;
        this.surfaceBuffer = null;
        this.texCoordBuffer = null;
        this.yTexture = null;
        this.uTexture = null;
        this.vTexture = null;
        try {
            const gl = createGL(canvas)!;
            this.gl = gl;
            gl.enable(gl.DEPTH_TEST);
            gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
            gl.depthFunc(gl.LEQUAL);
            gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
            this.glProgram = createProgramFromSources(this.gl, [
                vertexShaderSource,
                yuvShaderSource,
            ]);
            this.gl.useProgram(this.glProgram);
            this._initTextures();
        } catch (err) {
            throw new Error("create WebGL context failed");
        }
    }
    public draw(
        data: Uint8Array,
        width: number,
        height: number,
        rotate: 0 | 90 | 180 | 270 | 360 | undefined = 180,
    ): void {
        try {
            if (this.gl) {
                const yLength = width * height;
                const uLength = (width * height) / 4;
                const yPlane = new Uint8Array(data.buffer || data, 0, yLength);
                const uPlane = new Uint8Array(data.buffer || data, yLength, uLength);
                const vPlane = new Uint8Array(data.buffer || data, yLength + uLength, uLength);
                this._updateTexCoordLocation();
                this._updateYUVTexImage2D(width, height, yPlane, uPlane, vPlane);
                this._updatePositionLocation(width, height, rotate);
                this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);
            } else {
                throw new Error("WebGL context is null");
            }
        } catch (err) {
            //
        }
    }
    public destroy(): void {
        try {
            if (this.gl) {
                const glExtension = this.gl.getExtension("WEBGL_lose_context");
                if (glExtension) {
                    glExtension.loseContext();
                }
            }
        } catch (err) {
            //
        }
        this._deleteProgram(this.glProgram);
        this.glProgram = null;
        this.positionLocation = null;
        this.texCoordLocation = null;
        this._deleteTexture(this.yTexture);
        this._deleteTexture(this.uTexture);
        this._deleteTexture(this.vTexture);
        this.yTexture = null;
        this.uTexture = null;
        this.vTexture = null;
        this._deleteBuffer(this.texCoordBuffer);
        this._deleteBuffer(this.surfaceBuffer);
        this.texCoordBuffer = null;
        this.surfaceBuffer = null;
        this.gl = null;
    }
    public updateRotation(width: number, height: number, rotation: 0 | 90 | 180 | 270): void {
        this._updatePositionLocation(width, height, rotation);
    }
    private _initTextures(): void {
        if (this.gl && this.glProgram) {
            this.positionLocation = this.gl.getAttribLocation(this.glProgram, "a_position");
            this.texCoordLocation = this.gl.getAttribLocation(this.glProgram, "a_texCoord");
            this.surfaceBuffer = this.gl.createBuffer();
            this.texCoordBuffer = this.gl.createBuffer();
            this.gl.activeTexture(this.gl.TEXTURE0);
            this.yTexture = this.gl.createTexture();
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.yTexture);
            this.gl.texParameteri(
                this.gl.TEXTURE_2D,
                this.gl.TEXTURE_WRAP_S,
                this.gl.CLAMP_TO_EDGE,
            );
            this.gl.texParameteri(
                this.gl.TEXTURE_2D,
                this.gl.TEXTURE_WRAP_T,
                this.gl.CLAMP_TO_EDGE,
            );
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST);
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST);
            this.gl.activeTexture(this.gl.TEXTURE1);
            this.uTexture = this.gl.createTexture();
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.uTexture);
            this.gl.texParameteri(
                this.gl.TEXTURE_2D,
                this.gl.TEXTURE_WRAP_S,
                this.gl.CLAMP_TO_EDGE,
            );
            this.gl.texParameteri(
                this.gl.TEXTURE_2D,
                this.gl.TEXTURE_WRAP_T,
                this.gl.CLAMP_TO_EDGE,
            );
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST);
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST);
            this.gl.activeTexture(this.gl.TEXTURE2);
            this.vTexture = this.gl.createTexture();
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.vTexture);
            this.gl.texParameteri(
                this.gl.TEXTURE_2D,
                this.gl.TEXTURE_WRAP_S,
                this.gl.CLAMP_TO_EDGE,
            );
            this.gl.texParameteri(
                this.gl.TEXTURE_2D,
                this.gl.TEXTURE_WRAP_T,
                this.gl.CLAMP_TO_EDGE,
            );
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MIN_FILTER, this.gl.NEAREST);
            this.gl.texParameteri(this.gl.TEXTURE_2D, this.gl.TEXTURE_MAG_FILTER, this.gl.NEAREST);
            const y = this.gl.getUniformLocation(this.glProgram, "Ytex");
            this.gl.uniform1i(y, 0);
            const u = this.gl.getUniformLocation(this.glProgram, "Utex");
            this.gl.uniform1i(u, 1);
            const v = this.gl.getUniformLocation(this.glProgram, "Vtex");
            this.gl.uniform1i(v, 2);
        }
    }
    private _updateTexCoordLocation(): void {
        if (this.gl && this.texCoordLocation !== null) {
            // Update texCoord location
            this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.texCoordBuffer);
            this.gl.bufferData(
                this.gl.ARRAY_BUFFER,
                new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]),
                this.gl.STATIC_DRAW,
            );
            this.gl.enableVertexAttribArray(this.texCoordLocation);
            this.gl.vertexAttribPointer(this.texCoordLocation, 2, this.gl.FLOAT, false, 0, 0);
        }
    }
    private _updatePositionLocation(
        width: number,
        height: number,
        rotation?: 0 | 90 | 180 | 270 | 360,
    ): void {
        if (this.gl && this.positionLocation !== null && this.glProgram) {
            this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.surfaceBuffer);
            this.gl.enableVertexAttribArray(this.positionLocation);
            this.gl.vertexAttribPointer(this.positionLocation, 2, this.gl.FLOAT, false, 0, 0);
            let p1 = { x: 0, y: height };
            let p2 = { x: width, y: height };
            let p3 = { x: width, y: 0 };
            let p4 = { x: 0, y: 0 };
            if (rotation === 360) {
                p1 = { x: width, y: height };
                p2 = { x: 0, y: height };
                p3 = { x: 0, y: 0 };
                p4 = { x: width, y: 0 };
            }
            let pp1 = p1,
                pp2 = p2,
                pp3 = p3,
                pp4 = p4;
            if (rotation !== undefined) {
                switch (rotation) {
                    case 0: {
                        break;
                    }
                    case 90: {
                        pp1 = p2;
                        pp2 = p3;
                        pp3 = p4;
                        pp4 = p1;
                        break;
                    }
                    case 180: {
                        pp1 = p3;
                        pp2 = p4;
                        pp3 = p1;
                        pp4 = p2;
                        break;
                    }
                    case 270: {
                        pp1 = p4;
                        pp2 = p1;
                        pp3 = p2;
                        pp4 = p3;
                        break;
                    }
                    default:
                }
            }
            this.gl.bufferData(
                this.gl.ARRAY_BUFFER,
                new Float32Array([
                    pp1.x,
                    pp1.y,
                    pp2.x,
                    pp2.y,
                    pp4.x,
                    pp4.y,
                    pp4.x,
                    pp4.y,
                    pp2.x,
                    pp2.y,
                    pp3.x,
                    pp3.y,
                ]),
                this.gl.STATIC_DRAW,
            );
            const resolutionLocation = this.gl.getUniformLocation(this.glProgram, "u_resolution");
            this.gl.uniform2f(resolutionLocation, width, height);
        }
    }
    private _updateYUVTexImage2D(
        width: number,
        height: number,
        // @ts-ignore
        yplane: Uint8Array<ArrayBufferLike> | null,
        // @ts-ignore
        uplane: Uint8Array<ArrayBufferLike> | null,
        // @ts-ignore
        vplane: Uint8Array<ArrayBufferLike> | null,
    ): void {
        if (this.gl) {
            let e;
            this.gl.activeTexture(this.gl.TEXTURE0);
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.yTexture);
            this.gl.pixelStorei(this.gl.UNPACK_ALIGNMENT, 1);
            this.gl.texImage2D(
                this.gl.TEXTURE_2D,
                0,
                this.gl.LUMINANCE,
                width,
                height,
                0,
                this.gl.LUMINANCE,
                this.gl.UNSIGNED_BYTE,
                yplane,
            );
            e = this.gl.getError();
            if (e !== this.gl.NO_ERROR) {
                console.warn("upload y plane ", width, height, yplane?.byteLength, " error", e);
            }
            this.gl.activeTexture(this.gl.TEXTURE1);
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.uTexture);
            this.gl.texImage2D(
                this.gl.TEXTURE_2D,
                0,
                this.gl.LUMINANCE,
                width / 2,
                height / 2,
                0,
                this.gl.LUMINANCE,
                this.gl.UNSIGNED_BYTE,
                uplane,
            );
            e = this.gl.getError();
            if (e !== this.gl.NO_ERROR) {
                console.warn("upload u plane ", width, height, uplane?.byteLength, " error", e);
            }
            this.gl.activeTexture(this.gl.TEXTURE2);
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.vTexture);
            this.gl.texImage2D(
                this.gl.TEXTURE_2D,
                0,
                this.gl.LUMINANCE,
                width / 2,
                height / 2,
                0,
                this.gl.LUMINANCE,
                this.gl.UNSIGNED_BYTE,
                vplane,
            );
            e = this.gl.getError();
            if (e !== this.gl.NO_ERROR) {
                console.warn("upload v plane ", width, height, vplane?.byteLength, " error", e);
            }
        }
    }
    private _deleteTexture(texture: WebGLTexture | null): void {
        if (texture && this.gl) {
            this.gl.deleteTexture(texture);
        }
    }
    private _deleteBuffer(buffer: WebGLBuffer | null): void {
        if (buffer && this.gl) {
            this.gl.deleteBuffer(buffer);
        }
    }
    private _deleteProgram(program: WebGLProgram | null): void {
        if (program && this.gl) {
            this.gl.deleteProgram(program);
        }
    }
}

const vertex = [-1, -1, 0, 1, -1, 0.0, 1, 1, 0.0, -1, 1, 0.0];
const vertexIndice = [0, 1, 2, 0, 2, 3];
const triangleTexCoords = [0.0, 0.0, 1.0, 0.0, 1.0, 1.0, 0.0, 1.0];
const createShader = function (
    gl: WebGLRenderingContext,
    shaderSource: string,
    shaderType: number,
): WebGLShader {
    // compile the vertex shader
    const shader = gl.createShader(shaderType);
    if (shader) {
        gl.shaderSource(shader, shaderSource);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            throw new Error(`Compile shader failed: ${gl.getShaderInfoLog(shader)}`);
        }
        return shader;
    } else {
        throw new Error("create Shader failed");
    }
};
const initShaders = function (gl: WebGLRenderingContext): {
    vShader: WebGLShader;
    fShader: WebGLShader;
    glProgram: WebGLProgram;
} {
    const VSHADER_SOURCE =
        "attribute vec3 aPos;                    " +
        "attribute vec2 aVertexTextureCoord;     " +
        "varying mediump vec2 vTextureCoord;       " +
        "void main(void){                        " +
        "    gl_Position = vec4(aPos, 1);        " +
        "    vTextureCoord = aVertexTextureCoord;" +
        "}";
    const FSHADER_SOURCE =
        "varying mediump vec2 vTextureCoord;                                               " +
        "uniform sampler2D uSampler;                                                     " +
        "void main(void) {                                                               " +
        "    gl_FragColor = texture2D(uSampler, vec2(vTextureCoord.s, vTextureCoord.t)); " +
        "}";
    // compile shaders
    const vertexShader = createShader(gl, VSHADER_SOURCE, gl.VERTEX_SHADER);
    const fragmentShader = createShader(gl, FSHADER_SOURCE, gl.FRAGMENT_SHADER);
    const vShader = vertexShader;
    const fShader = fragmentShader;
    if (!vertexShader || !fragmentShader) {
        throw new Error("initialize shader failed");
    }
    // create program
    const glProgram = gl.createProgram();
    if (glProgram) {
        // attach and link shaders to the program
        gl.attachShader(glProgram, vertexShader);
        gl.attachShader(glProgram, fragmentShader);
        gl.linkProgram(glProgram);
        if (gl.getProgramParameter(glProgram, gl.LINK_STATUS)) {
            gl.useProgram(glProgram);
            return {
                vShader,
                fShader,
                glProgram,
            };
        } else {
            throw new Error("initialize shader program failed");
        }
    } else {
        throw new Error("create shader program failed");
    }
};
const initUniforms = function (
    gl: WebGLRenderingContext,
    glProgram: WebGLProgram,
): WebGLUniformLocation | null {
    return gl.getUniformLocation(glProgram, "uSampler");
};
const initTexture = function (gl: WebGLRenderingContext): WebGLTexture {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    // c++ addon 层拷贝数据时翻转,这里不做翻转
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return texture;
};
const initTextureAttr = function (gl: WebGLRenderingContext, glProgram: WebGLProgram): void {
    // vertex data
    const vertexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertex), gl.STATIC_DRAW);
    // indice data
    const vertexIndiceBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, vertexIndiceBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(vertexIndice), gl.STATIC_DRAW);
    // set position attribute
    const aVertexPosition = gl.getAttribLocation(glProgram, "aPos");
    gl.vertexAttribPointer(aVertexPosition, 3, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(aVertexPosition);
    // texture coordinate data
    const trianglesTexCoordBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, trianglesTexCoordBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(triangleTexCoords), gl.STATIC_DRAW);
    // set texture coordinate attribute
    const vertexTexCoordAttribute = gl.getAttribLocation(glProgram, "aVertexTextureCoord");
    gl.enableVertexAttribArray(vertexTexCoordAttribute);
    gl.vertexAttribPointer(vertexTexCoordAttribute, 2, gl.FLOAT, false, 0, 0);
};
export class RGBACanvas3DRenderer {
    private gl: WebGLRenderingContext | null = null;
    private texture: WebGLTexture;
    public constructor(canvas: HTMLCanvasElement) {
        try {
            this.gl = createGL(canvas)!;
            const { glProgram } = initShaders(this.gl);
            initUniforms(this.gl, glProgram);
            initTextureAttr(this.gl, glProgram);
            this.texture = initTexture(this.gl);
        } catch (err) {
            throw new Error("create WebGL context failed");
        }
    }
    public draw(data: Uint8Array, width: number, height: number): void {
        try {
            if (this.gl) {
                const gl = this.gl;
                gl.canvas.width = width;
                gl.canvas.height = height;
                gl.viewport(0, 0, width, height);
                gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
                gl.bindTexture(gl.TEXTURE_2D, this.texture);
                gl.texImage2D(
                    gl.TEXTURE_2D,
                    0,
                    gl.RGBA,
                    width,
                    height,
                    0,
                    gl.RGBA,
                    gl.UNSIGNED_BYTE,
                    data,
                );
                gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
            } else {
                throw new Error("WebGL context is null");
            }
        } catch (err) {
            //
        }
    }

    public destroy(): void {
        this.gl = null;
    }

    public hasContent(): boolean {
        if (!this.gl) {
            return false;
        }
        const { width, height } = this.gl.canvas;
        this.gl.finish();
        const pixels = new Uint8Array(width * height * 4);
        this.gl.readPixels(0, 0, width, height, this.gl.RGBA, this.gl.UNSIGNED_BYTE, pixels);

        const stride = 4 * 4; // 每隔4个像素采样一次
        let validCount = 0;
        let sampleCount = 0;

        for (let i = 0; i < pixels.length; i += stride) {
            const r = pixels[i];
            const g = pixels[i + 1];
            const b = pixels[i + 2];
            const a = pixels[i + 3];

            if (a === 0) {
                sampleCount++;
                continue;
            }

            // 将 0 或 255 视为异常/空白范围，仅 1..254 判定为“有内容”的证据
            const rValid = r > 0 && r < 255;
            const gValid = g > 0 && g < 255;
            const bValid = b > 0 && b < 255;

            if (rValid || gValid || bValid) {
                validCount++;
            }
            sampleCount++;
        }

        // 占比阈值，避免极少量噪点干扰
        const ratio = sampleCount ? validCount / sampleCount : 0;
        return ratio > 0.001;
    }
}
